import { useEffect, useRef, useState } from "react";
import { conversionConfig, releaseReady } from "./siteConfig";

type Stage = "invite" | "group";

const capabilities = [
  ["SIG-01", "实时通知", "集中浏览夏令营、预推免、正式推免、宣讲会与开放日信息。", "发现信号"],
  ["MAT-02", "AI 推荐", "结合学校、专业与关注方向，匹配可能相关的招生信息，结果仅供参考。", "匹配目标"],
  ["UNI-03", "学校大全", "按学校集中查看相关通知，减少在不同院系网站之间反复切换。", "建立坐标"],
  ["CAL-04", "保研日历", "按日期查看报名开始、截止和活动时间，关键节点一眼掌握。", "锁定节点"],
  ["ACT-05", "申请清单", "收藏感兴趣的通知，记录申请、入营和结果状态，避免进度混乱。", "推进任务"],
  ["DOC-06", "材料整理", "归集简历、个人陈述、推荐信、成绩单和英语证明等申请材料。", "备齐装备"],
  ["COM-07", "交流社群", "注册后进入交流群，获取产品使用指引与信息同步。", "保持联络"],
] as const;

const faqs = [
  {
    question: "保研信息网主要能解决什么问题？",
    answer: "它把分散的高校通知、AI 信息匹配、学校查询、保研日历和申请清单放在同一条使用路径里，帮助你更系统地发现信息、安排节点和记录进度。",
  },
  {
    question: "AI 推荐的结果可以直接作为择校结论吗？",
    answer: "不可以。AI 推荐用于缩小检索范围，只能作为参考。院校选择还需要结合个人背景、招生要求、研究方向和风险偏好综合判断。",
  },
  {
    question: "日历里的时间一定准确吗？",
    answer: "日历用于辅助管理报名开始、截止和活动节点。高校通知可能调整，提交申请前仍需回到学校或院系官方页面核对最新要求。",
  },
  {
    question: "为什么需要先注册再查看群二维码？",
    answer: "交流群用于向已完成邀请注册的同学提供产品使用指引和信息同步。本页只在当前浏览会话中记录你进行到哪一步，不采集身份信息。",
  },
] as const;

function QrPanel({ src, kind }: { src: string; kind: "邀请" | "交流群" }) {
  if (src) {
    return (
      <div className="qr-panel has-qr">
        <img src={src} alt={`${kind}二维码`} />
        <span>可扫码或长按识别</span>
      </div>
    );
  }

  return (
    <div className="qr-panel is-placeholder" aria-label={`${kind}二维码待提供`}>
      <div className="placeholder-frame" aria-hidden="true">
        <i /><i /><i /><i />
      </div>
      <strong>{kind}二维码待提供</strong>
      <span>占位内容 · 不可扫码</span>
    </div>
  );
}

function ConversionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [stage, setStageState] = useState<Stage>(() => {
    try {
      return sessionStorage.getItem("baoyan_conversion_stage") === "group" ? "group" : "invite";
    } catch {
      return "invite";
    }
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const setStage = (nextStage: Stage) => {
    setStageState(nextStage);
    try {
      sessionStorage.setItem("baoyan_conversion_stage", nextStage);
    } catch {
      // Session storage can be disabled by the browser; the flow still works in memory.
    }
    requestAnimationFrame(() => {
      const target = nextStage === "group" ? "back-to-invite" : "confirm-registration";
      document.getElementById(target)?.focus({ preventScroll: true });
    });
  };

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.closest("[hidden]"));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      lastFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className="conversion-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversion-title"
        aria-describedby="conversion-description"
        ref={dialogRef}
      >
        <div className="dialog-head">
          <div>
            <p>ACCESS SEQUENCE / TWO STEPS</p>
            <h2 id="conversion-title">注册与进群</h2>
          </div>
          <button className="close-dialog" type="button" aria-label="关闭弹窗" onClick={onClose} ref={closeRef}>×</button>
        </div>

        <div className="dialog-progress" aria-label="注册进群流程">
          <div className={`progress-step ${stage === "invite" ? "is-active" : ""}`}>
            <strong>第一步 · 完成注册</strong>
            使用个人邀请入口
          </div>
          <div className={`progress-step ${stage === "group" ? "is-active" : ""}`}>
            <strong>第二步 · 进入交流群</strong>
            注册后显示群二维码
          </div>
        </div>

        <div className="dialog-body" hidden={stage !== "invite"}>
          <QrPanel src={conversionConfig.inviteQrSrc} kind="邀请" />
          <div className="step-copy">
            <p className="step-code">STEP / 01</p>
            <h3>先用邀请入口完成注册</h3>
            <p id="conversion-description">使用微信扫码，或在手机端长按识别小程序码。完成后返回本页，继续查看交流群入口。</p>
            <ol className="step-list">
              <li data-index="01">扫码或长按识别小程序码</li>
              <li data-index="02">完成注册后返回本页面</li>
              <li data-index="03">点击“我已完成注册”查看群二维码</li>
            </ol>
            <div className="modal-actions">
              <button className="modal-action is-primary" id="confirm-registration" type="button" onClick={() => setStage("group")}>我已完成注册</button>
            </div>
            {!releaseReady && <p className="prototype-note">当前为本地待接入状态，正式发布前将替换真实链接与二维码。</p>}
          </div>
        </div>

        <div className="dialog-body" hidden={stage !== "group"}>
          <QrPanel src={conversionConfig.groupQrSrc} kind="交流群" />
          <div className="step-copy">
            <p className="step-code">STEP / 02</p>
            <h3>注册完成，继续进入交流群</h3>
            <p>扫码进入交流群，继续获取信息同步、保研日历使用指引和申请节点提醒。</p>
            <ol className="step-list">
              <li data-index="01">长按识别或使用另一台设备扫码</li>
              <li data-index="02">入群后根据群内指引完善关注方向</li>
              <li data-index="03">收藏保研日历并持续核对高校官方通知</li>
            </ol>
            <div className="modal-actions">
              <button className="modal-action is-secondary" id="back-to-invite" type="button" onClick={() => setStage("invite")}>返回上一步</button>
              <a className="modal-action is-primary" href={conversionConfig.calendarUrl} target="_blank" rel="noreferrer">查看保研日历</a>
            </div>
            <p className="prototype-note">群二维码有效期由运营方维护，失效后需及时替换。</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  return (
    <>
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <div className="page-shell">
        <header className="site-header">
          <div className="header-inner">
            <a className="brand-plate" href="#top" aria-label="返回保研信息网介绍站首页">
              <img src="/assets/baoyan-logo.png" alt="保研信息网" />
            </a>
            <nav className="nav-links" aria-label="页面导航">
              <a href="#problems">为什么需要</a>
              <a href="#capabilities">核心能力</a>
              <a href="#product">产品界面</a>
              <a href="#workflow">使用流程</a>
              <a href="#faq">常见问题</a>
            </nav>
            <button className="header-cta" type="button" onClick={openModal}>扫码注册</button>
          </div>
        </header>

        <main id="main-content">
          <section className="hero" id="top">
            <div className="hero-inner">
              <div className="hero-copy">
                <p className="eyebrow">Baoyan intelligence console</p>
                <h1>保研信息，<span>别再靠运气。</span></h1>
                <p className="hero-lead">聚合夏令营、预推免与九推通知，用 AI 匹配可能相关的项目，再把报名开始、截止时间和申请进度放进同一条行动路径。</p>
                <div className="hero-actions">
                  <button className="primary-cta" type="button" onClick={openModal}>扫码注册，解锁进群入口</button>
                  <a className="secondary-cta" href={conversionConfig.calendarUrl} target="_blank" rel="noreferrer">先看保研日历</a>
                </div>
                <p className="hero-meta"><span>高校通知聚合</span><span>AI 信息匹配</span><span>关键节点管理</span></p>
              </div>

              <div className="command-stage entrance-sequence" aria-label="保研信息作战室产品界面示意">
                <div className="command-topbar"><span className="live-mark">Signal route active</span><span className="stage-index">BYW / CONSOLE 01</span></div>
                <div className="screen-window">
                  <div className="scan-line" aria-hidden="true" />
                  <img src="/assets/product/product-overview.png" alt="保研先知院校汇总、功能首页和保研资料库界面" />
                  <div className="screen-note"><strong>从信息源到行动节点</strong>真实产品界面示意，以实际产品为准。</div>
                </div>
                <div className="signal-route" aria-label="产品工作路径">
                  <div className="route-node"><strong>发现通知</strong>高校信号源</div>
                  <div className="route-node"><strong>匹配目标</strong>AI 推荐</div>
                  <div className="route-node"><strong>锁定节点</strong>保研日历</div>
                  <div className="route-node"><strong>推进申请</strong>申请清单</div>
                </div>
                <span className="stage-corner">INTERFACE PREVIEW / ACTUAL PRODUCT PREVAILS</span>
              </div>
            </div>
          </section>

          <section className="problem-section" id="problems">
            <div className="section-inner">
              <p className="section-kicker">The information gap</p>
              <h2 className="section-title">真正消耗你的，不只是申请，而是散落在各处的信息。</h2>
              <div className="problem-grid">
                <article className="problem-item"><span className="problem-index">PROBLEM / 01</span><h3>通知藏得太深</h3><p>学校、学院和研究所各自发布信息，靠手动刷新很容易遗漏关键补充通知。</p></article>
                <article className="problem-item"><span className="problem-index">PROBLEM / 02</span><h3>目标选得太盲</h3><p>专业名称和招生院系并不总是一一对应，单靠关键词搜索可能错过交叉方向。</p></article>
                <article className="problem-item"><span className="problem-index">PROBLEM / 03</span><h3>截止来得太快</h3><p>报名开始、材料准备、活动时间同时推进，缺少统一视图就容易被动救火。</p></article>
              </div>
            </div>
          </section>

          <section className="capability-section" id="capabilities">
            <div className="section-inner">
              <div className="capability-heading">
                <div><p className="section-kicker">Mission capabilities</p><h2 className="section-title">把“我是不是漏了什么”，变成一条看得见的行动路径。</h2></div>
                <p>信息网负责发现，AI 帮助筛选，日历管理节点，申请清单记录行动。每项能力都围绕同一个目标：减少无效搜索，把时间留给真正的准备。</p>
              </div>
              <div className="capability-matrix" role="list" aria-label="保研信息网核心功能">
                {capabilities.map(([code, title, description, state]) => (
                  <article className="capability-row" role="listitem" key={code}>
                    <span className="cap-code">{code}</span><h3>{title}</h3><p>{description}</p><span className="cap-state">{state}</span>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="proof-section" id="product">
            <div className="section-inner">
              <p className="section-kicker">Product evidence</p>
              <h2 className="section-title">不是概念图，是正在使用的真实产品界面。</h2>
              <div className="proof-layout">
                <figure className="proof-panel">
                  <img src="/assets/product/application-ai.png" alt="申请清单、专业汇总和 AI 推荐界面" />
                  <figcaption className="proof-caption"><strong>清单、专业与 AI 推荐</strong><span>界面示意，以实际产品为准。</span></figcaption>
                </figure>
                <figure className="proof-panel is-secondary">
                  <img src="/assets/product/mini-program-notices.jpg" alt="保研先知小程序通知列表示例" />
                  <figcaption className="proof-caption"><strong>手机端查看通知</strong><span>界面示意，以实际产品为准。</span></figcaption>
                </figure>
              </div>
            </div>
          </section>

          <section className="workflow-section" id="workflow">
            <div className="section-inner">
              <div className="workflow-heading"><p className="section-kicker">Four-step route</p><h2 className="section-title">从注册到管理申请，按一条路径推进。</h2></div>
              <ol className="workflow-list">
                <li><span>01</span><div><strong>完成邀请注册</strong><p>通过本页邀请入口进入注册流程。</p></div></li>
                <li><span>02</span><div><strong>设置关注方向</strong><p>结合学校、专业和兴趣方向筛选信息。</p></div></li>
                <li><span>03</span><div><strong>进入交流群</strong><p>完成注册后扫码进入社群，获取使用指引。</p></div></li>
                <li><span>04</span><div><strong>管理日历与清单</strong><p>核对关键日期，持续更新自己的申请状态。</p></div></li>
              </ol>
            </div>
          </section>

          <section className="faq-section" id="faq">
            <div className="section-inner faq-layout">
              <div><p className="section-kicker">Field manual</p><h2 className="section-title">使用前，先把这些问题说清楚。</h2><p className="faq-intro">工具能降低信息整理成本，但不会替你做决定。所有申请动作，都应以高校官方要求为最终依据。</p></div>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <details key={faq.question} open={index === 0}>
                    <summary><span>Q{String(index + 1).padStart(2, "0")}</span>{faq.question}</summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="conversion-section">
            <div className="section-inner">
              <div className="conversion-panel">
                <div><p className="section-kicker">Access sequence</p><h2>先完成注册，<br />再进入交流群。</h2><p>通过邀请入口完成注册后，返回本页确认，即可查看交流群二维码。群内继续获取信息同步和使用指引。</p></div>
                <button className="primary-cta" type="button" onClick={openModal}>开始两步流程</button>
              </div>
            </div>
          </section>
        </main>

        <footer className="footer">
          <div className="footer-inner">
            <div><strong>保研信息网 · 保研信息作战室</strong><span>聚合信息，管理节点，推进申请。</span></div>
            <nav aria-label="页脚链接"><a href="https://pc.baoyanwang.com.cn/" target="_blank" rel="noreferrer">访问官方网站</a><a href={conversionConfig.calendarUrl} target="_blank" rel="noreferrer">打开保研日历</a></nav>
            <p>本站信息仅供参考，具体时间、资格、材料与录取要求以各高校官方通知为准。AI 推荐不构成申请或择校建议。</p>
          </div>
        </footer>
      </div>
      <ConversionModal open={modalOpen} onClose={closeModal} />
    </>
  );
}
